/**
 * CprMeasurementsOverlay — composite IOverlayObject that draws all diameter
 * lines and length arrows on a CPR (straightened/stretched) view.
 *
 * Renders:
 * - Diameters: full-width horizontal lines with right-aligned labels
 * - Lengths: dashed vertical lines with outward-pointing arrows, lane-assigned
 */

import type { Centerline } from '../../domain/centerline';
import type { IViewport } from '../../viewports';
import type { IOverlayObject, SizingDisplayData, SizingDisplayProperties } from '../types';
import { snapToPixel } from './canvas-utils';

// ============================================================================
// Internal Types
// ============================================================================

interface DiameterItem {
    nodeId: number;
    normalizedY: number;
    value: number;
    name: string;
}

interface LengthItem {
    id: string;
    startNormalizedY: number;
    endNormalizedY: number;
    value: number;
    name: string;
    lane: number;
    /** Provisional in-progress draft preview — drawn white, without a value label. */
    preview?: boolean;
}

export interface CprMeasurementsOverlayConfig {
    readonly id: string;
}

// ============================================================================
// Overlay
// ============================================================================

export class CprMeasurementsOverlay implements IOverlayObject {
    readonly id: string;
    readonly zIndex = 100;
    readonly interactive = false;
    readonly clipByCamera = false;
    visible = true;
    worldPosition: [number, number, number] | null = null;
    canvasPosition: { x: number; y: number } | null = null;

    private diameters: DiameterItem[] = [];
    private lengths: LengthItem[] = [];

    // Styling
    private readonly backdropColor = 'rgba(0,0,0,0.6)';
    private readonly diameterColor = '#5fbcea';
    private readonly diameterTextColor = '#5fbcea';
    private readonly lengthColor = '#09c0a8';
    private readonly previewColor = '#ffffff';
    private readonly font = "14px 'JetBrains Mono', monospace";

    constructor(config: CprMeasurementsOverlayConfig) {
        this.id = config.id;
    }

    // ========================================================================
    // Public API
    // ========================================================================

    updateData(data: SizingDisplayData, _properties: SizingDisplayProperties): void {
        const centerline = this.getActiveCenterline(data);
        if (!centerline) {
            this.diameters = [];
            this.lengths = [];
            return;
        }

        this.diameters = this.extractDiameters(data, centerline);
        this.lengths = this.extractLengths(data, centerline);

        const preview = this.extractDraftPreviewLength(data, centerline);
        if (preview) this.lengths.push(preview);

        this.assignLanes(this.lengths);
    }

    hitTest(): boolean {
        return false;
    }

    render(ctx: CanvasRenderingContext2D, viewport: IViewport): void {
        if (!this.visible || (this.diameters.length === 0 && this.lengths.length === 0)) return;

        const overlayLayer = viewport.getOverlayLayer();
        const canvasWidth = overlayLayer.getCssWidth();
        const canvasHeight = overlayLayer.getCssHeight();

        if (canvasWidth <= 0 || canvasHeight <= 0) return;

        // Compute visible world width from canvas edges for diameter→pixel conversion
        const worldLeft = viewport.canvasToWorld(0, canvasHeight / 2);
        const worldRight = viewport.canvasToWorld(canvasWidth, canvasHeight / 2);
        const worldWidth = Math.sqrt(
            (worldRight[0] - worldLeft[0]) ** 2 +
                (worldRight[1] - worldLeft[1]) ** 2 +
                (worldRight[2] - worldLeft[2]) ** 2
        );

        ctx.save();

        for (const diameter of this.diameters) {
            this.renderDiameter(ctx, diameter, canvasWidth, canvasHeight, worldWidth);
        }

        for (const length of this.lengths) {
            this.renderLength(ctx, length, canvasWidth, canvasHeight);
        }

        ctx.restore();
    }

    // ========================================================================
    // Data Extraction
    // ========================================================================

    private getActiveCenterline(data: SizingDisplayData): Centerline | null {
        const { activeCenterlineId, centerlines } = data;
        if (activeCenterlineId !== null) {
            return centerlines.get(activeCenterlineId) ?? null;
        }
        if (centerlines.size > 0) {
            return centerlines.values().next().value ?? null;
        }
        return null;
    }

    private extractDiameters(data: SizingDisplayData, centerline: Centerline): DiameterItem[] {
        if (centerline.totalLength <= 0) return [];

        const items: DiameterItem[] = [];
        for (const [, measurement] of data.measurements) {
            const ctx = measurement.context;
            if (
                ctx.type !== 'diameter' &&
                ctx.type !== 'min-max-diameter' &&
                ctx.type !== 'custom-diameter'
            ) {
                continue;
            }

            const nodeId = ctx.nodeId;
            const nodeIndex = centerline.getIndexByNodeId(nodeId);
            if (nodeIndex < 0) continue;

            const normalizedY = centerline.getDistanceAt(nodeIndex) / centerline.totalLength;

            items.push({
                nodeId,
                normalizedY,
                value: measurement.value,
                name: measurement.shortLabel,
            });
        }

        return items;
    }

    private extractLengths(data: SizingDisplayData, centerline: Centerline): LengthItem[] {
        if (centerline.totalLength <= 0) return [];

        const items: LengthItem[] = [];

        for (const [id, measurement] of data.measurements) {
            const ctx = measurement.context;
            if (ctx.type !== 'length' && ctx.type !== 'custom-length') continue;

            const startIndex = centerline.getIndexByNodeId(ctx.fromNodeId);
            const endIndex = centerline.getIndexByNodeId(ctx.toNodeId);
            if (startIndex < 0 || endIndex < 0) continue;

            const startNormalizedY = centerline.getDistanceAt(startIndex) / centerline.totalLength;
            const endNormalizedY = centerline.getDistanceAt(endIndex) / centerline.totalLength;

            items.push({
                id,
                startNormalizedY,
                endNormalizedY,
                value: measurement.value,
                name: measurement.shortLabel,
                lane: 0,
            });
        }

        return items;
    }

    /**
     * Provisional length lane for the in-progress custom-length draft: from the
     * placed start to the current cursor position. Returns null unless both
     * endpoints resolve on the active centerline (mirrors the 3D tube being
     * hidden when the measurement isn't computable).
     */
    private extractDraftPreviewLength(data: SizingDisplayData, centerline: Centerline): LengthItem | null {
        const draft = data.draft;
        if (!draft || draft.kind !== 'length' || !draft.previewLengthNodeIds) return null;
        if (centerline.totalLength <= 0) return null;

        const [startNodeId, endNodeId] = draft.previewLengthNodeIds;
        const startIndex = centerline.getIndexByNodeId(startNodeId);
        const endIndex = centerline.getIndexByNodeId(endNodeId);
        if (startIndex < 0 || endIndex < 0) return null;

        return {
            id: '__draft-preview-length__',
            startNormalizedY: centerline.getDistanceAt(startIndex) / centerline.totalLength,
            endNormalizedY: centerline.getDistanceAt(endIndex) / centerline.totalLength,
            value: 0,
            name: '',
            lane: 0,
            preview: true,
        };
    }

    // ========================================================================
    // Lane Assignment
    // ========================================================================

    private assignLanes(lengths: LengthItem[]): void {
        if (lengths.length === 0) return;

        const items = lengths
            .map((item) => ({
                item,
                start: Math.min(item.startNormalizedY, item.endNormalizedY),
                end: Math.max(item.startNormalizedY, item.endNormalizedY),
                span: Math.abs(item.endNormalizedY - item.startNormalizedY),
            }))
            .sort((a, b) => b.span - a.span);

        const lanes: (typeof items)[number][][] = [];

        while (items.length > 0) {
            const lane: (typeof items)[number][] = [];
            lanes.push(lane);

            lane.push(items[0]);
            items.splice(0, 1);

            for (let i = 0; i < items.length; i++) {
                const candidate = items[i];
                const overlaps = lane.some((x) => !(candidate.end <= x.start || candidate.start >= x.end));

                if (!overlaps) {
                    lane.push(candidate);
                    items.splice(i, 1);
                    i--;
                }
            }
        }

        lanes.forEach((lane, laneIndex) => {
            lane.forEach((entry) => {
                entry.item.lane = laneIndex;
            });
        });
    }

    // ========================================================================
    // Diameter Rendering
    // ========================================================================

    private renderDiameter(
        ctx: CanvasRenderingContext2D,
        item: DiameterItem,
        canvasWidth: number,
        canvasHeight: number,
        worldWidth: number
    ): void {
        const y = snapToPixel(canvasHeight * item.normalizedY, 1);

        // Full-width horizontal line
        ctx.strokeStyle = this.diameterColor;
        ctx.lineWidth = 1;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvasWidth, y);
        ctx.stroke();
        ctx.setLineDash([]);

        // Label setup
        const padding = 10;
        const textX = canvasWidth - padding;
        const nameText = item.name;
        const valueText = `${item.value.toFixed(1)} mm`;
        const combinedText = `${nameText}: ${valueText}`;

        ctx.font = this.font;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'right';

        const lineHeight = 16;
        const backdropPadding = 4;
        const combinedTextWidth = ctx.measureText(combinedText).width;

        // Compute vessel pixel extent: diameter spans equally from center in straightened view
        const vesselDiameterPx = worldWidth > 0 ? (item.value / worldWidth) * canvasWidth : 0;
        const vesselRightEdge = canvasWidth / 2 + vesselDiameterPx / 2;
        const labelLeftEdge = textX - combinedTextWidth;
        const overlapsVessel = labelLeftEdge < vesselRightEdge + padding;

        if (overlapsVessel) {
            // Two-line layout: name above the line, value below
            const nameWidth = ctx.measureText(nameText).width;
            const valueWidth = ctx.measureText(valueText).width;
            const maxWidth = Math.max(nameWidth, valueWidth);

            // Name above
            const nameBackdropX = textX - maxWidth - backdropPadding;
            const nameBackdropY = y - lineHeight - backdropPadding;
            ctx.fillStyle = this.backdropColor;
            ctx.fillRect(
                nameBackdropX,
                nameBackdropY,
                maxWidth + backdropPadding * 2,
                lineHeight + backdropPadding
            );
            ctx.fillStyle = this.diameterTextColor;
            ctx.fillText(nameText, textX, y - lineHeight / 2);

            // Value below
            const valueBackdropY = y + backdropPadding / 2;
            ctx.fillStyle = this.backdropColor;
            ctx.fillRect(
                nameBackdropX,
                valueBackdropY,
                maxWidth + backdropPadding * 2,
                lineHeight + backdropPadding
            );
            ctx.fillStyle = this.diameterTextColor;
            ctx.fillText(valueText, textX, y + lineHeight / 2 + backdropPadding / 2);
        } else {
            // Single-line layout: "Name: value mm" above the line
            const backdropWidth = combinedTextWidth + backdropPadding * 2;
            const backdropHeight = lineHeight + backdropPadding;
            const backdropX = textX - combinedTextWidth - backdropPadding;
            const backdropY = y - backdropPadding - lineHeight;

            ctx.fillStyle = this.backdropColor;
            ctx.fillRect(backdropX, backdropY, backdropWidth, backdropHeight);

            ctx.fillStyle = this.diameterTextColor;
            ctx.fillText(combinedText, textX, y - lineHeight / 2);
        }
    }

    // ========================================================================
    // Length Rendering
    // ========================================================================

    private renderLength(
        ctx: CanvasRenderingContext2D,
        item: LengthItem,
        canvasWidth: number,
        canvasHeight: number
    ): void {
        const laneSpacing = 0.1 * canvasWidth;
        const baseX = 0.1 * canvasWidth;
        const x = snapToPixel(baseX + item.lane * laneSpacing, 1);

        const y1 = snapToPixel(canvasHeight * item.startNormalizedY, 1);
        const y2 = snapToPixel(canvasHeight * item.endNormalizedY, 1);
        const midY = (y1 + y2) / 2;

        if (Math.abs(y2 - y1) < 1) return;

        const arrowSize = 8;
        const dy = y2 - y1;
        const dirY = dy / Math.abs(dy);

        // Provisional draft preview: continuous white line + arrows, no value label.
        if (item.preview) {
            ctx.strokeStyle = this.previewColor;
            ctx.fillStyle = this.previewColor;
            ctx.lineWidth = 1;
            ctx.setLineDash([]);

            ctx.beginPath();
            ctx.moveTo(x, y1);
            ctx.lineTo(x, y2);
            ctx.stroke();

            this.drawArrowHead(ctx, x, y1 - dirY, dirY, arrowSize);
            this.drawArrowHead(ctx, x, y2 + dirY, -dirY, arrowSize);
            return;
        }

        const lineHeight = 14;
        const backdropPadding = 4;
        const labelTop = midY - lineHeight / 2 - backdropPadding;
        const labelBottom = midY + lineHeight / 2 + backdropPadding;

        // Vertical line — top segment
        ctx.strokeStyle = this.lengthColor;
        ctx.lineWidth = 1;
        ctx.setLineDash([]);

        ctx.beginPath();
        ctx.moveTo(x, y1);
        ctx.lineTo(x, labelTop);
        ctx.stroke();

        // Vertical line — bottom segment
        ctx.beginPath();
        ctx.moveTo(x, labelBottom);
        ctx.lineTo(x, y2);
        ctx.stroke();

        ctx.setLineDash([]);

        // Outward-pointing arrow heads at endpoints
        ctx.fillStyle = this.lengthColor;
        this.drawArrowHead(ctx, x, y1 - dirY, dirY, arrowSize);
        this.drawArrowHead(ctx, x, y2 + dirY, -dirY, arrowSize);

        // Value label at midpoint, to the right of the line
        const valueText = `${item.value.toFixed(1)} mm`;
        ctx.font = this.font;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';

        const textWidth = ctx.measureText(valueText).width;
        const labelX = x - textWidth / 2;

        // Backdrop
        ctx.fillStyle = this.backdropColor;
        ctx.fillRect(
            labelX - backdropPadding,
            midY - lineHeight / 2 - backdropPadding,
            textWidth + backdropPadding * 2,
            lineHeight + backdropPadding * 2
        );

        // Text
        ctx.fillStyle = this.lengthColor;
        ctx.fillText(valueText, labelX, midY);
    }

    // ========================================================================
    // Arrow Head Helper
    // ========================================================================

    private drawArrowHead(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        direction: number,
        size: number
    ): void {
        const angle = Math.PI / 6; // 30 degrees
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x - size * Math.sin(angle), y + direction * size * Math.cos(angle));
        ctx.lineTo(x + size * Math.sin(angle), y + direction * size * Math.cos(angle));
        ctx.closePath();
        ctx.fill();
    }
}
